import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import Navbar from './components/common/Navbar';

// Import auth initialization
import { initializeAuth, startAuthMonitoring, stopAuthMonitoring } from './services/setupAuth';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import Dashboard from './pages/Dashboard';
import Accounts from './pages/Accounts';
import Transfer from './pages/Transfer';
import Transactions from './pages/Transactions';
import ProfileSettings from './pages/ProfileSettings';
import AboutPrivacy from './pages/AboutPrivacy';
import InternationalTransfer from './pages/InternationalTransfer';
import InternationalTransferVerification from './pages/InternationalTransferVerification';
import AdminPanel from './pages/AdminPanel';
import VerificationStatus from './pages/VerificationStatus';
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";

// Import CSS files
import './App.css';
import './index.css';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [initError, setInitError] = useState(null);

  // Initialize authentication when app starts
  useEffect(() => {
    console.log('🚀 App initializing...');
    
    const initAuth = async () => {
      try {
        // Initialize CSRF token and auth state
        await initializeAuth();
        
        // Start monitoring auth status (refreshes token, checks validity)
        startAuthMonitoring();
        
        console.log('✅ App initialization complete');
        setIsLoading(false);
      } catch (error) {
        console.error('❌ App initialization failed:', error);
        setInitError(error.message || 'Failed to initialize app');
        setIsLoading(false);
      }
    };
    
    initAuth();
    
    // Cleanup on unmount
    return () => {
      stopAuthMonitoring();
    };
  }, []);

  // Show loading or error state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-neutral-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary-600 mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-primary-800 mb-2">Loading Banking App</h1>
          <p className="text-primary-600">Initializing application...</p>
        </div>
      </div>
    );
  }

  if (initError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-white flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 border border-red-200">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">❌</span>
            </div>
            <h1 className="text-2xl font-bold text-red-700 mb-2">Initialization Error</h1>
            <p className="text-red-600 mb-4">{initError}</p>
          </div>
          <button 
            onClick={() => window.location.reload()}
            className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <AuthProvider>
        
        <div className="min-h-screen bg-gradient-to-br from-primary-50 to-neutral-100">
          {/* Navbar shown on all pages */}
          <Navbar />

          {/* CSS Test - Remove when Tailwind is working */}
          <div className="hidden test-blue"></div>
          <div className="hidden test-red"></div>

          {/* Content with padding for fixed navbar and banner */}
          <div className="pt-20 px-4">
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/home" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/verify-email/:token" element={<VerifyEmail />} />
              <Route path="/about" element={<AboutPrivacy />} />
              <Route path="/verification-status" element={<VerificationStatus />} />
              <Route path="/transfer/international/verification" element={<InternationalTransferVerification />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />

              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts"
                element={
                  <ProtectedRoute>
                    <Accounts />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/transfer"
                element={
                  <ProtectedRoute>
                    <Transfer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/transfer/international"
                element={
                  <ProtectedRoute>
                    <InternationalTransfer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/transfer/international/:id/verify"
                element={
                  <ProtectedRoute>
                    <InternationalTransferVerification />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/transactions"
                element={
                  <ProtectedRoute>
                    <Transactions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfileSettings />
                  </ProtectedRoute>
                }
              />

              {/* Admin Routes - Protected with Admin Check */}
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminPanel />
                  </AdminRoute>
                }
              />

              {/* 404 Page */}
              <Route path="*" element={
                <div className="container mx-auto px-4 py-16">
                  <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl p-8 border border-primary-100">
                    <div className="text-center mb-6">
                      <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-3xl text-primary-600">🔍</span>
                      </div>
                      <h1 className="text-4xl font-bold text-primary-800 mb-2">404</h1>
                      <p className="text-xl text-primary-600 mb-6">Page not found</p>
                      <p className="text-gray-600 mb-8">
                        The page you're looking for doesn't exist or has been moved.
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <a 
                        href="/" 
                        className="inline-block bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold text-center transition-colors"
                      >
                        ← Go back home
                      </a>
                      <button 
                        onClick={() => window.history.back()}
                        className="inline-block border-2 border-primary-600 text-primary-600 hover:bg-primary-50 px-6 py-3 rounded-lg font-semibold text-center transition-colors"
                      >
                        Go back
                      </button>
                    </div>
                  </div>
                </div>
              } />
            </Routes>
          </div>
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;